import type { FC } from "react";
import React from "react";
import classnames from "classnames";
import { hasWindow } from "@litres/utils";
import { useTranslation } from "@litres-libs/i18n";
import { StyledLink } from "@litres/ui-kit/link";
import { useApi } from "../../../hooks/useApi";

import { CloseIcon } from "../../../../static/icons/CloseIcon";
import styles from "./SearchHistory.module.scss";

import type { ISearchHistoryProps } from "../types";
import { trackerSpotlightClickHistoryItem } from "../utils";

export const SearchHistory: FC<ISearchHistoryProps> = ({
  history,
  setHistoryRefreshed,
  deleteItemFromLastSearchStorage,
  searchHistoryListRef,
  setLoading,
  isDesktop = true,
  onClose,
}) => {
  const { t } = useTranslation();
  const { clearSearchHistory } = useApi();
  const reversedHistory = history.slice(0, 10).reverse();

  const handleClearHistoryClick = (): void => {
    clearSearchHistory();
    setHistoryRefreshed(true);
  };

  const handleHistoryItemClick = (value: string): void => {
    setLoading(true);

    trackerSpotlightClickHistoryItem(value);

    if (hasWindow) {
      window.location.assign(`/search/?q=${encodeURIComponent(value)}`);
    }
  };

  return (
    <ul
      className={classnames(styles.historyListAB, {
        [styles.mobileList]: !isDesktop,
      })}
      ref={searchHistoryListRef}
      data-testid="header__search-history"
      role="list"
      aria-label={t("История поиска")}
      id="searchResult"
    >
      {reversedHistory.length > 0 && (
        <div className={styles.info} role="listitem">
          <p className={styles.previousSearches} id="historyClearLabel">
            {t("Ранее вы искали")}
          </p>

          <StyledLink
            className={styles.clear}
            onClick={handleClearHistoryClick}
            aria-describedby="historyClearLabel"
          >
            {isDesktop ? t("Очистить историю") : t("Очистить")}
          </StyledLink>
        </div>
      )}

      {reversedHistory?.map(({ value }) => (
        <li
          className={styles.item}
          key={value}
          tabIndex={0}
          onClick={(): void => {
            handleHistoryItemClick(value);
          }}
          role="listitem"
          aria-label={`Искать ${value}`}
        >
          <span className={styles.itemText}>{value}</span>
          <button
            tabIndex={0}
            aria-label={t("Убрать")}
            className={styles.closeIcon}
            onClick={(e: React.MouseEvent): void => {
              e.stopPropagation();
              e.preventDefault();
              deleteItemFromLastSearchStorage(value);
            }}
          >
            <CloseIcon />
          </button>
        </li>
      ))}

      {!isDesktop && (
        <li
          className={styles.close}
          tabIndex={0}
          onClick={onClose}
          role="listitem"
        >
          {t("Закрыть")}
        </li>
      )}
    </ul>
  );
};
